import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, Button, TextField, InputAdornment, Chip, IconButton, Typography,
  FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, LinearProgress, alpha, Alert, Tooltip, Drawer,
} from '@mui/material';
import ErpDataGrid, { GeneralItemsGridFrame } from '../../components/common/ErpDataGrid';
import ListRowActionsBar from '../../components/common/ListRowActionsBar';
import {
  CloudUpload as UploadIcon, Search as SearchIcon, Download as DownloadIcon,
  Delete as DeleteIcon, InsertDriveFile as FileIcon, Close as CloseIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon, TextSnippet as TextIcon,
  Image as ImageIcon, Description as DocIcon, Warning as WarningIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api, { apiUpload, getApiBaseUrl } from '../../utils/api';
import { getAuthAccessToken } from '../../utils/demoPreviewStorage';
import { useAuth } from '../../context/AuthContext';
import { standardAuditColumns } from '../../utils/auditGridColumns';
import useListDetailOpenMode from '../../hooks/useListDetailOpenMode';
import useDocumentTypes, { labelForDocumentType, defaultDocumentTypeCode } from '../../hooks/useDocumentTypes';
import ListDetailOpenModeBar from '../../components/common/ListDetailOpenModeBar';
import { fetchDocumentPreviewContent } from '../../utils/documentPreviewContent';

function documentAssetUrl(docId, pathSegment) {
  const base = String(getApiBaseUrl() || '').replace(/\/+$/, '');
  return `${base}/documents/${docId}/${pathSegment}`;
}

// Map file extensions to icons
const getFileIcon = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf':
      return <PdfIcon sx={{ color: '#ef4444' }} />;
    case 'txt':
      return <TextIcon sx={{ color: '#6b7280' }} />;
    case 'doc':
    case 'docx':
    case 'rtf':
      return <DocIcon sx={{ color: '#2563eb' }} />;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
      return <ImageIcon sx={{ color: '#10b981' }} />;
    default:
      return <FileIcon sx={{ color: '#6366f1' }} />;
  }
};

// Check if file can be previewed
const canPreview = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  return ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'txt', 'doc', 'docx', 'rtf'].includes(ext);
};

const DocumentsList = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { mode: docOpenMode, persistMode: persistDocOpenMode } = useListDetailOpenMode('documents');
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ document_type: '', lifecycle: '' });
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10, total: 0 });
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', document_type: 'other', description: '', file: null });
  
  // Document settings from admin
  const [docSettings, setDocSettings] = useState({
    maxSizeMB: 10,
    allowedTypes: ['pdf', 'txt', 'docx', 'doc', 'rtf']
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  // Preview state
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationError, setValidationError] = useState(null);
  const dragCounter = useRef(0);
  const dropZoneRef = useRef(null);
  const { documentTypes, loading: docTypesLoading } = useDocumentTypes();

  useEffect(() => {
    if (!documentTypes.length) return;
    const codes = new Set(documentTypes.map((t) => t.code));
    const def = defaultDocumentTypeCode(documentTypes);
    setUploadForm((f) => (codes.has(f.document_type) ? f : { ...f, document_type: def }));
    setFilters((fl) => (!fl.document_type || codes.has(fl.document_type) ? fl : { ...fl, document_type: '' }));
  }, [documentTypes]);

  // Fetch document settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings/document-settings');
        setDocSettings(response.data.data);
      } catch (error) {
        console.error('Failed to fetch document settings:', error);
      } finally {
        setSettingsLoaded(true);
      }
    };
    fetchSettings();
  }, []);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/documents', {
        params: { page: pagination.page + 1, limit: pagination.pageSize, search, ...filters }
      });
      setDocuments(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, search, filters]);

  useEffect(() => {
    const lc = searchParams.get('lifecycle');
    if (lc === 'deleted' || lc === 'archived' || lc === 'active') {
      setFilters((f) => (f.lifecycle === lc ? f : { ...f, lifecycle: lc }));
    }
  }, [searchParams]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Validate file against settings
  const validateFile = (file) => {
    const errors = [];
    const ext = file.name.split('.').pop().toLowerCase();
    const sizeMB = file.size / (1024 * 1024);

    // Check file type
    if (!docSettings.allowedTypes.includes(ext)) {
      errors.push(`File type ".${ext}" is not allowed. Allowed types: ${docSettings.allowedTypes.map(t => `.${t}`).join(', ')}`);
    }

    // Check file size
    if (sizeMB > docSettings.maxSizeMB) {
      errors.push(`File size (${sizeMB.toFixed(2)} MB) exceeds maximum allowed size of ${docSettings.maxSizeMB} MB`);
    }

    return errors;
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (!hasPermission('documents_add')) {
      toast.error('You do not have permission to upload documents');
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Validate all files first
      const invalidFiles = [];
      const validFiles = [];
      
      files.forEach(file => {
        const errors = validateFile(file);
        if (errors.length > 0) {
          invalidFiles.push({ name: file.name, errors });
        } else {
          validFiles.push(file);
        }
      });

      if (invalidFiles.length > 0) {
        const errorMessage = invalidFiles.map(f => `${f.name}: ${f.errors.join(', ')}`).join('\n');
        toast.error(`Invalid files:\n${errorMessage}`, { autoClose: 5000 });
      }

      if (validFiles.length === 1) {
        setUploadForm({ ...uploadForm, file: validFiles[0], title: validFiles[0].name.split('.')[0] });
        setValidationError(null);
        setUploadDialog(true);
      } else if (validFiles.length > 1) {
        handleBatchUpload(validFiles);
      }
    }
  };

  const handleBatchUpload = async (files) => {
    setUploading(true);
    setUploadProgress(0);
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        await apiUpload('/documents', file, {
          title: file.name.split('.')[0],
          document_type: defaultDocumentTypeCode(documentTypes),
          description: ''
        });
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to upload ${file.name}:`, error);
      }
      setUploadProgress(((i + 1) / files.length) * 100);
    }

    setUploading(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} document(s) uploaded successfully`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} document(s) failed to upload`);
    }
    
    fetchDocuments();
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    
    const errors = validateFile(file);
    if (errors.length > 0) {
      setValidationError(errors.join('. '));
      setUploadForm({ ...uploadForm, file: null });
    } else {
      setValidationError(null);
      setUploadForm({
        ...uploadForm,
        file: file,
        title: uploadForm.title || file.name.split('.')[0]
      });
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file) {
      toast.error('Please select a file');
      return;
    }

    // Re-validate before upload
    const errors = validateFile(uploadForm.file);
    if (errors.length > 0) {
      setValidationError(errors.join('. '));
      return;
    }

    try {
      setUploading(true);
      await apiUpload('/documents', uploadForm.file, {
        title: uploadForm.title || uploadForm.file.name.split('.')[0],
        document_type: uploadForm.document_type,
        description: uploadForm.description
      });
      toast.success('Document uploaded successfully');
      setUploadDialog(false);
      setUploadForm({ title: '', document_type: defaultDocumentTypeCode(documentTypes), description: '', file: null });
      setValidationError(null);
      fetchDocuments();
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const openPreviewForDoc = async (doc, surface) => {
    if (surface === 'page') {
      navigate(`/documents/${doc.id}/view`);
      return;
    }
    setPreviewDoc(doc);
    setPreviewDialog(surface === 'popup');
    setPreviewDrawerOpen(surface === 'side');
    setPreviewLoading(true);
    setPreviewContent(null);
    try {
      const content = await fetchDocumentPreviewContent(doc, { documentAssetUrl, getAuthAccessToken });
      setPreviewContent(content);
    } catch (error) {
      console.error('Preview error:', error);
      setPreviewContent({ type: 'error' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreview = (doc) => openPreviewForDoc(doc, 'popup');

  const handleGridRowClick = (params) => {
    const doc = params.row;
    if (docOpenMode === 'page') openPreviewForDoc(doc, 'page');
    else if (docOpenMode === 'popup') openPreviewForDoc(doc, 'popup');
    else openPreviewForDoc(doc, 'side');
  };

  // Cleanup blob URLs when preview closes
  const handleClosePreview = () => {
    if (previewContent?.isBlob && previewContent?.url) {
      URL.revokeObjectURL(previewContent.url);
    }
    setPreviewDialog(false);
    setPreviewDrawerOpen(false);
    setPreviewContent(null);
    setPreviewDoc(null);
  };

  const handleDownload = async (doc) => {
    try {
      const token = getAuthAccessToken();
      const response = await fetch(documentAssetUrl(doc.id, 'download'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        toast.error('Failed to download document');
        return;
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const lifecycleColors = { active: '#10b981', deleted: '#b45309', archived: '#7c3aed' };

  const lifecycleDisplay = (row) => {
    if (row.archived_at) return { label: 'Archived', key: 'archived' };
    if (row.is_marked_for_deletion) return { label: 'Deleted', key: 'deleted' };
    return { label: 'Active', key: 'active' };
  };

  const typeColors = {
    resume: '#6366f1',
    cover_letter: '#a855f7',
    client_agreement: '#ec4899',
    vendor_agreement: '#f59e0b',
    nda: '#ef4444',
    sow: '#3b82f6',
    po: '#10b981',
    invoice: '#8b5cf6',
    timesheet: '#0ea5e9',
    id_proof: '#14b8a6',
    work_authorization: '#d946ef',
    other: '#6b7280'
  };

  const columns = [
    {
      field: 'document_code',
      headerName: 'Reference',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace" sx={{ fontWeight: 500 }}>
          {params.value || '–'}
        </Typography>
      )
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getFileIcon(params.row.file_name)}
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      )
    },
    {
      field: 'lifecycle',
      headerName: 'Lifecycle',
      width: 110,
      sortable: false,
      renderCell: (params) => {
        const { label, key } = lifecycleDisplay(params.row);
        const c = lifecycleColors[key] || lifecycleColors.active;
        return (
          <Chip label={label} size="small" sx={{ bgcolor: `${c}20`, color: c, fontSize: '0.7rem', fontWeight: 500 }} />
        );
      }
    },
    {
      field: 'document_type',
      headerName: 'Type',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={labelForDocumentType(params.value, documentTypes)}
          size="small"
          sx={{
            bgcolor: `${typeColors[params.value] || '#6b7280'}20`,
            color: typeColors[params.value] || '#6b7280',
            fontSize: '0.7rem'
          }}
        />
      )
    },
    { field: 'file_name', headerName: 'File Name', width: 200 },
    {
      field: 'file_size',
      headerName: 'Size',
      width: 100,
      renderCell: (params) => formatFileSize(params.value)
    },
    ...standardAuditColumns(),
    {
      field: 'actions',
      headerName: 'Actions',
      width: 260,
      sortable: false,
      renderCell: (params) => {
        const doc = params.row;
        const ext = doc.file_name?.split('.').pop()?.toLowerCase();
        const isPdf = ext === 'pdf';
        const copyDownloadUrl = () => {
          const url = documentAssetUrl(doc.id, 'download');
          const token = getAuthAccessToken();
          const sep = url.includes('?') ? '&' : '?';
          const full = token ? `${url}${sep}token=${encodeURIComponent(token)}` : url;
          navigator.clipboard
            .writeText(full)
            .then(() => toast.success('Download URL copied'))
            .catch(() => toast.error('Could not copy'));
        };
        return (
          <ListRowActionsBar
            menuItems={[
              {
                label: 'Download file',
                icon: <DownloadIcon fontSize="small" />,
                onClick: () => handleDownload(doc),
              },
              {
                label: 'Copy download URL',
                icon: <ContentCopyIcon fontSize="small" />,
                onClick: copyDownloadUrl,
              },
            ]}
          >
            <Tooltip title={canPreview(doc.file_name) ? 'View / preview' : 'Preview not available for this type'}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canPreview(doc.file_name)}
                  color="primary"
                  onClick={() => handlePreview(doc)}
                >
                  <ViewIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {isPdf ? (
              <Tooltip title="Download PDF">
                <IconButton size="small" onClick={() => handleDownload(doc)} sx={{ color: 'error.main' }}>
                  <PdfIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {hasPermission('documents_delete') && (
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => handleDelete(doc.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </ListRowActionsBar>
        );
      },
    }
  ];

  // Format allowed types for display
  const getAllowedTypesDisplay = () => {
    return docSettings.allowedTypes.map(t => `.${t.toUpperCase()}`).join(', ');
  };

  // Generate accept string for file input
  const getAcceptString = () => {
    const mimeTypes = {
      pdf: 'application/pdf',
      txt: 'text/plain',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      rtf: 'application/rtf',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xls: 'application/vnd.ms-excel',
      csv: 'text/csv',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
    };
    return docSettings.allowedTypes.map(t => mimeTypes[t] || `.${t}`).join(',');
  };

  return (
    <Box
      ref={dropZoneRef}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{ position: 'relative', minHeight: '100%' }}
    >
      {/* Drag overlay */}
      {isDragging && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            border: '3px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <UploadIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
              Drop files here to upload
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Allowed: {getAllowedTypesDisplay()} (Max: {docSettings.maxSizeMB} MB)
            </Typography>
          </Box>
        </Box>
      )}

      {/* Upload progress bar */}
      {uploading && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Documents</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage resumes, agreements, and other documents
          </Typography>
        </Box>
        {hasPermission('documents_add') && (
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialog(true)}
            sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
          >
            Upload Document
          </Button>
        )}
      </Box>

      {/* File restrictions info */}
      {settingsLoaded && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Upload restrictions:</strong> Maximum file size: {docSettings.maxSizeMB} MB | Allowed file types: {getAllowedTypesDisplay()}
        </Alert>
      )}

      {/* Drag and Drop Zone */}
      {hasPermission('documents_add') && (
        <Card
          sx={{
            mb: 3,
            p: 4,
            border: '2px dashed',
            borderColor: 'divider',
            bgcolor: 'transparent',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
            },
          }}
          onClick={() => document.getElementById('file-upload-input').click()}
        >
          <input
            id="file-upload-input"
            type="file"
            multiple
            accept={getAcceptString()}
            hidden
            onChange={(e) => {
              const files = Array.from(e.target.files);
              // Validate all files
              const invalidFiles = [];
              const validFiles = [];
              
              files.forEach(file => {
                const errors = validateFile(file);
                if (errors.length > 0) {
                  invalidFiles.push({ name: file.name, errors });
                } else {
                  validFiles.push(file);
                }
              });

              if (invalidFiles.length > 0) {
                const errorMessage = invalidFiles.map(f => `${f.name}: ${f.errors.join(', ')}`).join('\n');
                toast.error(`Invalid files:\n${errorMessage}`, { autoClose: 5000 });
              }

              if (validFiles.length === 1) {
                setUploadForm({ ...uploadForm, file: validFiles[0], title: validFiles[0].name.split('.')[0] });
                setValidationError(null);
                setUploadDialog(true);
              } else if (validFiles.length > 1) {
                handleBatchUpload(validFiles);
              }
              e.target.value = '';
            }}
          />
          <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" color="text.primary" sx={{ fontWeight: 500 }}>
            Drag & drop files here
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to browse • Max {docSettings.maxSizeMB} MB • {getAllowedTypesDisplay()}
          </Typography>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search documents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Lifecycle</InputLabel>
            <Select
              value={filters.lifecycle}
              label="Lifecycle"
              onChange={(e) => setFilters({ ...filters, lifecycle: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="deleted">Deleted</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filters.document_type}
              label="Type"
              onChange={(e) => setFilters({ ...filters, document_type: e.target.value })}
              disabled={docTypesLoading && documentTypes.length === 0}
            >
              <MenuItem value="">All</MenuItem>
              {documentTypes.map((t) => (
                <MenuItem key={t.code} value={t.code}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ListDetailOpenModeBar value={docOpenMode} onChange={persistDocOpenMode} sx={{ flex: '1 1 auto' }} />
        </Box>
      </Card>

      {/* Data Grid */}
      <Card sx={{ overflow: 'hidden' }}>
        <GeneralItemsGridFrame>
          <ErpDataGrid
            listApplet
            columnOrderStorageKey="general-items-documents"
            rows={documents}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={pagination.total}
            paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
            onPaginationModelChange={(model) => setPagination({ ...pagination, ...model })}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            autoHeight={false}
            onRowClick={handleGridRowClick}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </GeneralItemsGridFrame>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => !uploading && setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Upload Document
          <IconButton onClick={() => !uploading && setUploadDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* File restrictions reminder */}
            <Grid item xs={12}>
              <Alert severity="info" icon={<WarningIcon />}>
                Max size: {docSettings.maxSizeMB} MB • Allowed: {getAllowedTypesDisplay()}
              </Alert>
            </Grid>

            {validationError && (
              <Grid item xs={12}>
                <Alert severity="error">{validationError}</Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={uploadForm.document_type}
                  label="Document Type"
                  onChange={(e) => setUploadForm({ ...uploadForm, document_type: e.target.value })}
                  disabled={docTypesLoading && documentTypes.length === 0}
                >
                  {documentTypes.map((t) => (
                    <MenuItem key={t.code} value={t.code}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: uploadForm.file ? 'primary.main' : validationError ? 'error.main' : 'divider',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: uploadForm.file ? (theme) => alpha(theme.palette.primary.main, 0.05) : 'transparent',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                  },
                }}
                onClick={() => document.getElementById('dialog-file-input').click()}
              >
                <input
                  id="dialog-file-input"
                  type="file"
                  accept={getAcceptString()}
                  hidden
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleFileSelect(e.target.files[0]);
                    }
                  }}
                />
                {uploadForm.file ? (
                  <Box>
                    {getFileIcon(uploadForm.file.name)}
                    <Typography variant="body1" sx={{ fontWeight: 500, mt: 1 }}>
                      {uploadForm.file.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(uploadForm.file.size)}
                    </Typography>
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadForm({ ...uploadForm, file: null });
                        setValidationError(null);
                      }}
                    >
                      Change File
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body1">Click to select a file</Typography>
                    <Typography variant="body2" color="text.secondary">
                      or drag and drop here
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setUploadDialog(false); setValidationError(null); }} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!uploadForm.file || uploading || validationError}
            startIcon={uploading ? null : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialog} 
        onClose={handleClosePreview} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {previewDoc && getFileIcon(previewDoc.file_name)}
            <Typography variant="h6">{previewDoc?.title || 'Document Preview'}</Typography>
          </Box>
          <Box>
            {previewDoc && (
              <Tooltip title="Download">
                <IconButton onClick={() => handleDownload(previewDoc)} sx={{ mr: 1 }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleClosePreview}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <LinearProgress sx={{ width: '50%' }} />
            </Box>
          ) : previewContent?.type === 'text' ? (
            <Box sx={{ p: 3, overflow: 'auto', flex: 1, bgcolor: 'background.default' }}>
              <pre style={{ 
                whiteSpace: 'pre-wrap', 
                wordBreak: 'break-word', 
                fontFamily: 'monospace',
                margin: 0,
                fontSize: '14px'
              }}>
                {previewContent.content}
              </pre>
            </Box>
          ) : previewContent?.type === 'html' ? (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <iframe
                srcDoc={previewContent.content}
                title={previewDoc?.title}
                style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
                sandbox="allow-same-origin"
              />
            </Box>
          ) : previewContent?.type === 'image' ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, p: 2 }}>
              <img 
                src={previewContent.url} 
                alt={previewDoc?.title} 
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </Box>
          ) : previewContent?.type === 'pdf' ? (
            <Box sx={{ flex: 1 }}>
              <iframe
                src={previewContent.url}
                title={previewDoc?.title}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </Box>
          ) : previewContent?.type === 'unsupported' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <FileIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Preview not available for this file type
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<DownloadIcon />} 
                onClick={() => handleDownload(previewDoc)}
                sx={{ mt: 2 }}
              >
                Download to View
              </Button>
            </Box>
          ) : previewContent?.type === 'error' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <WarningIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography variant="h6" color="error">
                Failed to load preview
              </Typography>
              <Button 
                variant="outlined" 
                startIcon={<DownloadIcon />} 
                onClick={() => handleDownload(previewDoc)}
                sx={{ mt: 2 }}
              >
                Try downloading instead
              </Button>
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>

      <Drawer
        anchor="right"
        open={previewDrawerOpen}
        onClose={handleClosePreview}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 'min(960px, 94vw)' },
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {previewDoc && getFileIcon(previewDoc.file_name)}
            <Typography variant="h6" noWrap>
              {previewDoc?.title || 'Document Preview'}
            </Typography>
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            {previewDoc && (
              <Tooltip title="Download">
                <IconButton onClick={() => handleDownload(previewDoc)} sx={{ mr: 1 }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleClosePreview}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 6 }}>
              <LinearProgress sx={{ width: '50%' }} />
            </Box>
          ) : previewContent?.type === 'text' ? (
            <Box sx={{ p: 3, overflow: 'auto', flex: 1, bgcolor: 'background.default' }}>
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  margin: 0,
                  fontSize: '14px',
                }}
              >
                {previewContent.content}
              </pre>
            </Box>
          ) : previewContent?.type === 'html' ? (
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 400 }}>
              <iframe
                srcDoc={previewContent.content}
                title={previewDoc?.title}
                style={{ width: '100%', height: '100%', minHeight: 480, border: 'none', background: '#fff' }}
                sandbox="allow-same-origin"
              />
            </Box>
          ) : previewContent?.type === 'image' ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, p: 2 }}>
              <img
                src={previewContent.url}
                alt={previewDoc?.title}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </Box>
          ) : previewContent?.type === 'pdf' ? (
            <Box sx={{ flex: 1, minHeight: 480 }}>
              <iframe
                src={previewContent.url}
                title={previewDoc?.title}
                style={{ width: '100%', height: '100%', minHeight: 480, border: 'none' }}
              />
            </Box>
          ) : previewContent?.type === 'unsupported' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <FileIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Preview not available for this file type
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload(previewDoc)}
                sx={{ mt: 2 }}
              >
                Download to View
              </Button>
            </Box>
          ) : previewContent?.type === 'error' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <WarningIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography variant="h6" color="error">
                Failed to load preview
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload(previewDoc)}
                sx={{ mt: 2 }}
              >
                Try downloading instead
              </Button>
            </Box>
          ) : null}
        </Box>
      </Drawer>
    </Box>
  );
};

export default DocumentsList;
